import React, { ReactNode } from "react";
import HistoryOutlined from "@ant-design/icons/HistoryOutlined";
import Button from "common/Buttons/Button";
import styled, { css, useTheme } from "styled-components";
import { RotateCcw } from "lucide-react";
import { Steps } from "@shared/features/bots/BotForm/common/types";
import { RESET_TAB_BUTTON_HEIGHT } from "@shared/features/bots/BotForm/common/constants";
import { useRestoreChangesButton } from "@shared/features/bots/BotForm/common/hooks";
import Dropdown from "common/Dropdown/Dropdown";
import { getBotFormRestoreButtonId } from "testIds.ts";

interface RestoreChangesButtonProps {
  tab: Steps;
  title?: ReactNode;
}

const RestoreChangesButton = ({ tab, title }: RestoreChangesButtonProps) => {
  const {
    shouldDisplay,
    resetData,
    isDropdown,
    onClick,
    dropdownOptions,
    restoreText,
  } = useRestoreChangesButton(tab);
  const { isNewLook } = useTheme();

  const titleComponent = title ? <TitleWrapper>{title}</TitleWrapper> : null;
  if (!shouldDisplay) {
    return null;
  }

  const resetButton = (
    <ResetButton
      data-testid={getBotFormRestoreButtonId(tab)}
      onClick={onClick}
      disabled={!resetData?.valuesChanged}
    >
      {isNewLook ? (
        <RotateCcw size={14} strokeWidth={1.75} />
      ) : (
        <HistoryOutlined />
      )}
      {restoreText}
    </ResetButton>
  );

  if (isDropdown) {
    const menuItems = dropdownOptions.map((option) => ({
      key: option.text,
      label: option.text,
      onClick: option.onClick,
      className: option.disabled ? "invalid" : undefined,
    }));
    const dropdown = (
      <Dropdown
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={["click"]}
      >
        {resetButton}
      </Dropdown>
    );
    if (isNewLook) {
      return (
        <NewLookRow>
          {title ?? <span />}
          {dropdown}
        </NewLookRow>
      );
    }
    return dropdown;
  }

  if (isNewLook) {
    // New look: the title and the restore button share one row in the flow,
    // so the button never covers the section's own switch.
    return (
      <NewLookRow>
        {title ?? <span />}
        {resetButton}
      </NewLookRow>
    );
  }

  return (
    <>
      {titleComponent}
      {resetButton}
    </>
  );
};

export default RestoreChangesButton;

const ResetButton = styled(Button)`
  position: absolute;
  right: 0;
  top: 0;
  font-size: 12px;
  height: ${RESET_TAB_BUTTON_HEIGHT}px;
  @media (max-width: 400px) {
    padding: 4px 8px;
  }
  ${(p) =>
    p.theme.isNewLook &&
    css`
      position: static;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      height: 30px;
      padding: 0 12px;
      font-size: 12.5px;
    `};
`;
const NewLookRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 30px;
  margin-bottom: 16px;
`;
const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  height: ${RESET_TAB_BUTTON_HEIGHT}px;
  margin-bottom: 12px;
`;
